import React from 'react';

const ChatbotSection: React.FC = () => {
    return (
        <section className="py-20 bg-white">
            <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                        AI Chatbots
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Intelligent conversational AI that engages customers 24/7 across multiple platforms
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Website Chatbot */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-primary rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-primary/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary transition-colors duration-300 mb-4">Website Chatbot</h3>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Missed customer inquiries, high bounce rates, and limited support availability</p>
                            </div>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">AI-powered website chatbot that provides instant responses, captures leads, and escalates when needed</p>
                            </div>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• 24/7 customer support</li>
                                    <li>• 40% reduction in response time</li>
                                    <li>• Increased lead generation</li>
                                    <li>• Improved customer satisfaction</li>
                                </ul>
                            </div>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Use Cases</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• Customer support</li>
                                    <li>• Product recommendations</li>
                                    <li>• Lead qualification</li>
                                    <li>• FAQ automation</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* WhatsApp Chatbot */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-secondary rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-secondary/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-secondary transition-colors duration-300 mb-4">WhatsApp Chatbot</h3>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Manual WhatsApp messaging, inconsistent responses, and limited scalability</p>
                            </div>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">Automated WhatsApp bot for customer service, notifications, and business communications</p>
                            </div>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• Reach 2B+ WhatsApp users</li>
                                    <li>• Automated notifications</li>
                                    <li>• Rich media support</li>
                                    <li>• High engagement rates</li>
                                </ul>
                            </div>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Use Cases</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• Order updates</li>
                                    <li>• Appointment reminders</li>
                                    <li>• Customer support</li>
                                    <li>• Marketing campaigns</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Customer Support Bot */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-dark/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-dark rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-dark/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-dark transition-colors duration-300 mb-4">Customer Support Bot</h3>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">High support costs, long wait times, and inconsistent service quality</p>
                            </div>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">Comprehensive support bot with ticket integration, knowledge base, and human handoff</p>
                            </div>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• 60% reduction in support costs</li>
                                    <li>• Instant resolution for common issues</li>
                                    <li>• Seamless human escalation</li>
                                    <li>• Multi-language support</li>
                                </ul>
                            </div>

                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 mb-2">Use Cases</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• Technical support</li>
                                    <li>• Account management</li>
                                    <li>• Billing inquiries</li>
                                    <li>• Product troubleshooting</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ChatbotSection;
